import type { StringResolver } from '@pct-ts/typing';
import { isString } from '@pct-ts/typeguard';

/**
 * Resolves a string value based on a resolver function or a static string.
 *
 * This function accepts either a string directly or a function that returns a string.
 * If a string is provided, it's returned as-is. If a function is provided, it's called
 * with the given parameter to generate the string.
 *
 * @param resolver - A string value or a function that resolves to a string
 * @param param - The parameter to pass to the resolver function
 * @returns The resolved string value
 *
 * @example
 * // Using a static string
 * const static = resolveString('Hello', {});
 * console.log(static); // Output: "Hello"
 *
 * @example
 * // Using a resolver function
 * const dynamic = resolveString(
 *   (user: { name: string }) => `Hello ${user.name}`,
 *   { name: 'John' }
 * );
 * console.log(dynamic); // Output: "Hello John"
 */
export function resolveString<T>(resolver: StringResolver<T>, param: T): string {
  if (isString(resolver)) {
    return resolver;
  }
  return resolver(param);
}
